import { type ComponentPropsWithRef } from 'react';

import type {
  VBaseTableColumnRenederer,
  VBaseTableData,
  VBaseTableProps,
} from '../VBaseTable';
import { VBaseTable } from '../VBaseTable';
import {
  useCheckable,
  useFilterableAndSortable,
  useRemovable,
  useRowClassName,
} from './hook';

const VInnerTable = <T extends VBaseTableData>({
  ref,
  ...rest
}: VBaseTableProps<T> &
  Omit<
    ComponentPropsWithRef<'div'>,
    'onClick' | 'onDoubleClick' | 'onScrollEnd'
  >) => {
  const { columnRenderer } = rest;
  const enhancedColumnRenderer = useFilterableAndSortable({
    columnRenderer: columnRenderer as VBaseTableColumnRenederer<T>,
  });
  const {
    columnList: checkedColumnList,
    columnRenderer: checkedColumnRenderer,
    columnStyleMap: checkedColumnStyleMap,
    cellRenderer: checkedCellRenderer,
  } = useCheckable({ ...rest, columnRenderer: enhancedColumnRenderer });

  const {
    columnList: removableColumnList,
    columnRenderer: removableColumnRenderer,
    columnStyleMap: removableColumnStyleMap,
    cellRenderer: removableCellRenderer,
  } = useRemovable({
    ...rest,
    columnList: checkedColumnList,
    columnRenderer: checkedColumnRenderer,
    columnStyleMap: checkedColumnStyleMap,
    cellRenderer: checkedCellRenderer,
  });

  const rowClassName = useRowClassName();

  return (
    <VBaseTable
      ref={ref}
      {...rest}
      columnList={removableColumnList}
      columnRenderer={removableColumnRenderer}
      columnStyleMap={removableColumnStyleMap}
      cellRenderer={removableCellRenderer}
      rowClassName={rowClassName}
    />
  );
};

export default VInnerTable;
